import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Form, Formik, FormikHelpers } from 'formik';
import merge from 'lodash.merge';

import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Dialog from '@material-ui/core/Dialog';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import SearchIcon from '@material-ui/icons/Search';
import { CircularProgress } from '@material-ui/core';

import compose from 'helpers/compose';
import withoutEnterSubmit from 'components/withoutEnterSubmit';
import ControlButtons from 'components/ControlButtons';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import FormTextField from 'components/FormFields/FormTextField';

import { EmployersStore } from 'stores/workflowStores/finances/employers/employersStore';
import { initialValues, Employer } from 'stores/workflowStores/finances/employers/model';

const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

interface Props {
  opened: boolean;
  handleClose: () => void;
  employerId?: string;
  refresh: () => void;
  employersStore: EmployersStore;
  detailMode: boolean;
}

const EmployerModal: React.FC<Props> = ({ employerId, opened, handleClose, employersStore, refresh, detailMode }) => {
  const { t } = useTranslation();
  let formSubmitting = false;
  const formKey = 'EmployerForm';
  const { fetchEmployer, handleSubmit, data } = employersStore;
  const [companyNameResolved, setCompanyNameResolved] = useState<boolean | undefined | null>(undefined);

  useEffect(() => {
    if (opened && employerId) {
      fetchEmployer(employerId);
    }
  }, [employerId, fetchEmployer, opened]);

  const onSubmit = async (values: Employer, formikHelpers: FormikHelpers<Employer>) => {
    const ok = await handleSubmit(values, formikHelpers);
    if (ok) {
      handleClose();
      refresh();
    }
  };

  const resetAndClose = () => {
    employersStore.resetLookupStatus();
    handleClose();
  };

  return (
    <>
      <Dialog maxWidth="md" open={opened}>
        <DialogHeader onClose={resetAndClose}>{t('employer_data')}</DialogHeader>
        <DialogContent>
          <Formik
            initialValues={data && employerId ? merge({}, initialValues, data) : initialValues}
            onSubmit={onSubmit}
            enableReinitialize
          >
            {(formikProps) => {
              formSubmitting = formikProps.isSubmitting;
              return (
                <FormWithoutEnterSubmit id={t(formKey)}>
                  <Grid container>
                    <Grid item xs={6}>
                      <FormTextField
                        name="companyNumber"
                        label={t('employer_companyNumber')}
                        maxLength={8}
                        disabled={detailMode && !!employerId}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={async () => {
                          setCompanyNameResolved(null);
                          const found = await employersStore.getEmployerFromARES(
                            formikProps.values.companyNumber,
                            formikProps,
                          );
                          setCompanyNameResolved(found);
                        }}
                        startIcon={companyNameResolved === null ? <CircularProgress size={20} /> : <SearchIcon />}
                        disabled={companyNameResolved === null}
                      >
                        {t('employer_search')}
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <FormTextField
                        name="companyName"
                        label={t('employer_companyName')}
                        disabled={companyNameResolved === null}
                        fast={false}
                      />
                    </Grid>
                  </Grid>
                </FormWithoutEnterSubmit>
              );
            }}
          </Formik>
        </DialogContent>
        <DialogActions>
          <ControlButtons
            isSubmitting={formSubmitting}
            nextText={t('general_save')}
            form={formKey}
            backText={t('general_cancel')}
            onBack={resetAndClose}
            onClick={() => {}}
          />
        </DialogActions>
      </Dialog>
      <ConfirmDialog noHeader isOpen={companyNameResolved === false} confirm={() => setCompanyNameResolved(undefined)}>
        {t(`employer_lookupStatus_${employersStore.lookupStatus}`)}
      </ConfirmDialog>
    </>
  );
};

export default compose(observer)(EmployerModal);
